<script>
/**
 * Type   : Modal component
 * Title  : Order 모달
 * Status : Actived
 * Description : Order의 수정에 사용하는 모달. Admin에서 사용
 * 
 * 업무흐름
 * 
 * 1. 택배사 목록, 도시 코드, 운송수단 코드, 오더상태 코드, 전자상거래유형 코드 조회
 * 2. Order 내 상품목록 조회
 * 3. 상태변경 이력조회
 * 
 */

import ModalCustomerSearch from "@/components/admin/common/modal/CustomerSearch.vue"
import PdfPreviewer from '@/components/admin/common/modal/PdfPreview.vue'


// 상품목록 초기값
const defaultItemSet = {
    barcode   : '',         // 바코드
    itemName  : '',         // 품명 (필수)
    brand     : '',         // 브랜드
    itemQty   : 1,          // 수량
    unit      : '',         // 단위 (예: 개, 박스 등)
    currency  : 'USD',      // 통화 (예: USD, KRW)
    unitPrice : 0.00,       // 단가
    weight    : 0.00,       // 중량
    url       : '',         // 상품 URL
    hsCode    : '',         // HS CODE
  };

export default {
  name: "ModalParcelInfoForm",
  props:{
    selectedParcel  : Array,  // 선택된 ORDER의 정보
    user            : String  // 사용자 구분 (admin, cus)
  },
  components: {
    ModalCustomerSearch,
    PdfPreviewer
  },
  computed: {
    isAdmin() {
      return Number(this.authLevel) >= 90
    },
    canCancelPacking() {
      return this.isAdmin && this.parcelInfo?.packingStatus === 'COMPLETED'
        && this.parcelInfo?.packingOwner === 'AONE'
        && !this.parcelInfo?.consoleId && this.parcelInfo?.tempConsolStatus !== 'LINKED'
    },
    packingStatusLabel() {
      const status = String(this.parcelInfo?.packingStatus || "").trim().toUpperCase()
      if (status === "COMPLETED") return "포장완료"
      if (status === "IN_PROGRESS") return "포장중"
      if (status === "READY") return "대기"
      return "-"
    },
    packingStatusColor() {
      const status = String(this.parcelInfo?.packingStatus || "").trim().toUpperCase()
      if (status === "COMPLETED") return "positive"
      if (status === "IN_PROGRESS") return "warning"
      if (status === "READY") return "primary"
      return "grey-6"
    },
    orderStatusLabel() {
      if (this.parcelInfo?.statusName) return this.parcelInfo.statusName
      const options = this.$store.getters.getOrderStatusCodeList || []
      const status = options.find(option => String(option.value) === String(this.parcelInfo?.status))
      return status?.label || this.parcelInfo?.status || '-'
    },
    orderStatusColor() {
      return 'primary'
    },
    consoleStatusLabel() {
      if (this.parcelInfo?.consoleId || this.parcelInfo?.tempConsolStatus === 'LINKED') return '연결됨'
      if (this.parcelInfo?.tempConsolStatus === 'READY') return '연결 대기'
      return '미연결'
    },
    consoleStatusColor() {
      if (this.parcelInfo?.consoleId || this.parcelInfo?.tempConsolStatus === 'LINKED') return 'positive'
      if (this.parcelInfo?.tempConsolStatus === 'READY') return 'warning'
      return 'grey-6'
    },
    itemTotalQty() {
      return this.itemList.reduce((sum, item) => sum + Number(item.itemQty || 0), 0)
    },
    packingOwnerLabel() {
      const owner = String(this.parcelInfo?.packingOwner || 'PARTNER').trim().toUpperCase()
      if (owner === 'AONE') return 'AONE'
      if (owner === 'PARTNER') return '파트너'
      return '-'
    },
    itemAmountsByCurrency() {
      const amounts = this.itemList.reduce((result, item) => {
        const currency = String(item.currency || '-').trim().toUpperCase() || '-'
        const quantity = Number(item.itemQty || 0)
        const unitPrice = Number(item.unitPrice || 0)
        const amount = Number.isFinite(quantity * unitPrice) ? quantity * unitPrice : 0
        result[currency] = (result[currency] || 0) + amount
        return result
      }, {})

      return Object.entries(amounts)
    },
    itemAmountSummary() {
      if (this.itemAmountsByCurrency.length === 0) return '-'

      const formatter = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 2 })
      return this.itemAmountsByCurrency
        .map(([currency, amount]) => `${currency} ${formatter.format(amount)}`)
        .join(' · ')
    },
    itemAmountWarning() {
      return this.itemAmountsByCurrency.length === 1
        && this.itemAmountsByCurrency[0][0] === 'USD'
        && this.itemAmountsByCurrency[0][1] > 150
    },
    // 오더의 무게 계산
    orderVolumeWeight() {
      return (this.parcelInfo.width * this.parcelInfo.height * this.parcelInfo.length) / (this.parcelInfo.freightMethod == '1' ? 6000 : 1000000)  // 항공일 경우
    },
    // 오더의 정보를 수정할 수 있는지 여부
    editable () {
      if(this.authLevel >= 90){
        return true
      } else { // 파트너는
        // M.B/L 번호가 없는 경우에만 수정 가능
        if(this.parcelInfo.mblNo === '' || this.parcelInfo.mblNo === null){
          return true
        } else {
          return false
        }
      }
    },
    statusEditable () {
      // 파트너의 경우
      if(this.authLevel === '80'){
        if(Number(this.parcelInfo.status) < 3){
          return true
        } else {
          return false
        }
      } else if (Number(this.authLevel) >= 90){ // 관리자는 모두 수정 가능
        return true
      }
      return false
    },
    orderStatusCodeListForPartner () {
      // 파트너의 경우 오더 상태코드를 제한한다
      return this.$store.getters.getOrderStatusCodeList.filter(code => { if(Number(code.value) < 3) {return code}})
    },
    canUseFulfillmentBarcode () {
      const session = this.$store.getters.getLocalSession || {}
      if (Number(session?.authLevel) >= 90 || Number(this.authLevel) >= 90) return true
      return this.toBool(session?.has_fulfillment_contract)
    },
    itemColumns () {
      if (this.canUseFulfillmentBarcode) return this.columns
      return this.columns.filter(column => column.name !== 'barcode')
    }
  },
  emits:[
    "close-modal",
    "refresh-list",
  ],

  data() {
    return {
      authLevel       : '00',
      parcelInfo      : this.selectedParcel[0],
      originalStatus  : this.selectedParcel[0].status,
      packingStatusUpdating: false,
      pendingPackingCancellation: null,
      parcelIds       : [],
      customerModal   : false,

      pdfModalShow    : false,

      // item list 관련 columns
      columns : [
        { name: 'barcode', label: '바코드', align: 'center', field: 'barcode' },
        { name: 'itemName', label: '상품명', align: 'center', field: 'itemName' },
        { name: 'brand', label: '브랜드', align: 'center', field: 'brand' },
        { name: 'itemQty', label: '수량', align: 'center', field: 'itemQty' },
        { name: 'unit', label: '단위', align: 'center', field: 'unit' },
        { name: 'currency', label: '통화', align: 'center', field: 'currency' },

        { name: 'unitPrice', label: '단가', align: 'center', field: 'unitPrice' },
        // { name: 'width', label: '넓이', align: 'center', field: 'width' },
        // { name: 'height', label: '높이', align: 'center', field: 'height' },
        // { name: 'length', label: '길이', align: 'center', field: 'length' },
        { name: 'weight', label: '중량', align: 'center', field: 'weight' },
        // { name: 'volumeWeight', label: '볼륨중량', align: 'center', field: 'volumeWeight'},
        // { name: 'url', label: '사이트 주소', align: 'center', field: 'url' },
        
        // { name: 'modiDate', label: '최종수정일', align: 'center', field: 'modiDate' },
        // { name: 'regDate', label: '등록일', align: 'center', field: 'regDate' },
        { 
          name: 'hsCode', 
          label: 'HS CODE', 
          align: 'center', 
          field: 'hsCode',  
          format: (val) => val && val.length > 6 ? val.substring(0, 6) : val,
        },
        {
          name: 'action',
          label: '삭제',
          field: 'action',
          align: 'center'
        }
      ],
      separator: 'cell',
      itemList : [],  // Order 내 상품목록 조회 결과
      defaultItemSet,
      historyColumns : [
        { name: 'statusLabel', label: '상태', align: 'center', field: 'statusLabel' },
        { name: 'updated_by_name', label: '변경자', align: 'center', field: 'updated_by_name' },
        { name: 'created_at', label: '수정일', align: 'center', field: 'created_at' },
      ],
      historyList : [], // Order 상태변경 이력조회 결과
    }
  },
  watch: {
    selectedParcel(newValue) {
      const nextParcel = newValue?.[0]
      if (!nextParcel || nextParcel === this.parcelInfo) return

      this.parcelInfo = nextParcel
      this.originalStatus = nextParcel.status
      this.pendingPackingCancellation = null
    }
  },
  methods : {
    scrollToSection(sectionId) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },

    applyPackingState(state) {
      if (Number(state?.orderId) !== Number(this.parcelInfo.id)) return
      Object.assign(this.parcelInfo, {
        packingStatus: state.packingStatus,
        tempConsolStatus: state.tempConsolStatus,
        packingCompletedAt: state.packingCompletedAt,
        waybillPrinted: state.waybillPrinted
      })
    },
    async confirmPackingStatusChange() {
      if (!this.isAdmin || this.packingStatusUpdating) return
      if (this.pendingPackingCancellation) return this.changePackingStatus()
      if (!this.canCancelPacking) return
      const id = Number(this.parcelInfo.id)
      this.packingStatusUpdating = true
      let state
      try {
        const res = await this.$http({ method: 'GET', url: `/api/parcel/${id}/packing-status`, timeout: 15000 })
        if (res?.data?.ok !== true) throw new Error('현재 포장 상태를 확인할 수 없습니다.')
        state = res.data.data
        this.applyPackingState(state)
        if (Number(this.parcelInfo.id) !== id || !this.canCancelPacking) {
          throw new Error('현재 취소할 수 있는 포장완료 상태가 아닙니다. 목록을 다시 확인해 주세요.')
        }
      } catch (error) {
        this.$q.notify({ type: 'negative', message: error.response?.data?.message || error.message })
        this.$emit('refresh-list')
        return
      } finally { this.packingStatusUpdating = false }
      this.$q.dialog({
        title: '포장완료 취소',
        message: '포장완료 → 포장대기. 포장·출력 확인 상태와 임시콘솔 대기를 초기화합니다. 출력된 운송장은 현장에서 회수해 주세요. 취소 사유를 입력해 주세요.',
        prompt: {
          model: '', type: 'textarea', maxlength: 255, counter: true,
          isValid: value => {
            const length = String(value || '').trim().length
            return length > 0 && length <= 255
          }
        },
        ok: { label: '포장완료 취소', color: 'primary' },
        cancel: { label: '닫기', color: 'grey-6' }, persistent: true
      }).onOk(reason => this.changePackingStatus(reason, { id, revision: state.revision }))
    },
    async changePackingStatus(reason, snapshot) {
      if (!this.isAdmin || this.packingStatusUpdating) return
      if (!this.pendingPackingCancellation) {
        const trimmed = String(reason || '').trim()
        if (!snapshot || Number(this.parcelInfo.id) !== snapshot.id || !trimmed || trimmed.length > 255) return
        const cryptoApi = window.crypto
        if (typeof cryptoApi?.randomUUID !== 'function') {
          this.$q.notify({ type: 'negative', message: '안전한 요청 번호를 생성할 수 없습니다. HTTPS 접속 상태를 확인해 주세요.' })
          return
        }
        this.pendingPackingCancellation = {
          id: snapshot.id,
          data: { targetStatus: 'READY', reason: trimmed, expectedRevision: snapshot.revision, requestKey: cryptoApi.randomUUID() }
        }
      }
      const request = this.pendingPackingCancellation
      this.packingStatusUpdating = true
      try {
        const res = await this.$http({ method: 'PATCH', url: `/api/parcel/${request.id}/packing-status`,
          data: request.data, timeout: 15000, name: '[ Axios / ParcelInfoForm / 포장완료 취소 ]' })
        if (res?.data?.ok !== true || !res.data.data?.packingStatus) throw new Error('취소 처리 결과를 확인할 수 없습니다.')
        this.applyPackingState(res.data.data)
        this.pendingPackingCancellation = null
        this.$emit('refresh-list')
        this.$q.notify({ type: 'positive', message: res.data.data.replayed
          ? '이전 취소 요청의 처리 결과를 확인했습니다. 현재 상태를 확인해 주세요.'
          : '포장완료를 취소하고 포장대기로 변경했습니다.' })
      } catch (error) {
        const status = Number(error.response?.status)
        if (status >= 400 && status < 500) {
          this.pendingPackingCancellation = null
          this.$emit('refresh-list')
        }
        this.$q.notify({ type: 'negative', message: error.response?.data?.message
          || '처리 결과를 확인할 수 없습니다. 같은 요청 결과 확인을 눌러 주세요.' })
      } finally { this.packingStatusUpdating = false }
    },

    toBool(value) {
      if (typeof value === 'boolean') return value
      if (typeof value === 'number') return value === 1
      const normalized = String(value ?? '').trim().toLowerCase()
      return ['1', 'true', 'y', 'yes'].includes(normalized)
    },

    hasPrintableWeight(weight) {
      const value = Number(weight)
      return Number.isFinite(value) && value > 0
    },

    
    /**
     * Title        : Order 내 상품목록 조회
     * Status       : Actived
     * Description  : OrderId 기준으로 조회한 내 상품목록을 조회한다.
     * 
     * Actived
     */
    async getItemList(){
      let payload = {
        orderId : this.parcelInfo.id
      }

      let config = {
        method  : "POST",
        url     : "/api/parcel/getItemList",
        data    : payload,
        name    : "[ Axios / ParcelInfoForm / Order 내 상품목록 조회 ]"
      }
  
      await this.$http(config)
        .then((res) => {
          this.itemList = res.data
        })
        .catch((err) => {
          this.$log.debug(err)
          alert('Order 내 상품목록 조회에 실패했습니다')
      })

    },


    /**
     * Title        : ORDER 상태변경 이력조회
     * Status       : Actived
     * Description  : ORDER 상태변경 이력을 조회한다.
     */
     async getOrderHistory () {
      let payload = {
        orderId : this.parcelInfo.id
      }

      let config = {
        method  : "POST",
        url     : "/api/parcel/getOrderHistory",
        data    : payload,
        name    : "[ Axios / ParcelInfoForm / Order 상태변경 이력조회 ]"
      }
  
      await this.$http(config)
        .then((res) => {
          this.$log.debug("Order 상태변경 이력조회 결과 = ", res.data)
          // TODO : 상태변경 이력조회 결과를 화면에 표시한다
          this.historyList = res.data
        })
        .catch((err) => {
          this.$log.debug(err)
          alert('Order 상태변경 이력조회에 실패했습니다')
      })
    },


    /**************************
     * ORDER UPDATE 관련기능
     **************************/
    /** 
     * Title        : 배송상품 수정 Submit
     * Status       : Actived
     * 
     * Description  : 
     *  조회된 배송상품의 정보를 수정한다
     *  검증 로직을 추가하여 필수항목 체크 및 URL 형식 검증을 수행한다.
     *  1차 검증 : Vue의 v-model과 rules를 사용한 필수항목 체크
     *  2차 검증 : 수기검증 로직을 통해 추가적인 제약사항을 확인한다.
     *          - 아이템 최소 1개 이상
     *          - 각 아이템 필수값(상품명, 수량, 금액) 체크 
     *          - URL 형식 검증
     *          - 상태/오더ID 교차조건 확인
     */ 
    async onSubmit () {
      this.$log.debug("Order update exectued")
      const ok = await this.$refs.orderForm.validate()
      if (!ok) {
        this.$q.notify({ type: 'negative', message: '필수 항목을 확인해 주세요.' })
        return
      }

      // 2차 수기검증 (테이블 행 존재, URL 형식, 상태별 제약 등)
      const extraError = this.validateExtras()
      if (extraError) {
        this.$q.notify({ type: 'negative', message: extraError })
        return
      }

      // 기존 로직 유지 (오더ID 없을 때 분기 포함)
      this.updateOrderConfirm()
    },


    /** 
     * Title        : 부가정보 검증
     * Status       : Actived
     * Description  : 아이템 목록, URL 형식, 상태/오더ID 교차조건 등을 검증한다.
     * 
     * @returns {string|null} 에러 메시지 또는 null (문제없음)
     */
    validateExtras () {
      // 1) 아이템 최소 1개
      if (!this.itemList || this.itemList.length === 0) {
        return '상품은 최소 1개 이상이어야 합니다.'
      }
      // 2) 각 아이템 필수값(상품명) 수기체크 (rules가 있지만 안전망)
      for (const [idx, it] of this.itemList.entries()) {
        if (!it.itemName) return `${idx + 1}번째 상품의 상품명을 입력해 주세요.`
        if (!it.itemQty || Number(it.itemQty) <= 0) return `${idx + 1}번째 상품의 수량은 1 이상이어야 합니다.`
        if (it.unitPrice === null || it.unitPrice === undefined || it.unitPrice === '' || Number.isNaN(Number(it.unitPrice))) {
          return `${idx + 1}번째 상품의 금액을 입력해 주세요.`
        }
      }
      // 3) 상태/오더ID 교차조건 (이미 updateOrderConfirm에서 처리하지만, 선제 알림용)
      if (this.parcelInfo.status === '02' && !this.parcelInfo.orderId) {
        // 여기서는 메시지만 노출, 실제 분기는 updateOrderConfirm에서
        // return '진행상태가 04면 Order No.가 필요합니다.'
      }
      return null
    },


    /** 
     * Title        : 배송상품 수정
     * Status       : Actived
     * Description  : 조회된 배송상품의 정보를 수정한다
     *                Order number가 부여되지 않은 경우 Order number의 자동 채번또는 직접입력을 유도하며 
     *                Order number가 빈값은 불가능하다.
     */ 
    async updateOrderConfirm() {
      const status = this.parcelInfo.status

      if(status === '02' && (this.parcelInfo.orderId === '' || this.parcelInfo.orderId === null)){
        // Order number 부여여부 확인
        this.$q.dialog({
          title: '확인',
          message: 'Order number가 없습니다.\n직접 입력 하시겠습니까?',
          ok : { label : '직접입력', color : 'primary' },
          cancel: { label : '자동채번', color : 'secondary' },
          persistent: true // ESC로 닫기 방지 (옵션)
        }).onOk(() => { // 직접입력
          // 로직 취소 
          return false;    
        }).onCancel(async () => {
          this.updateOrder()
          this.$emit('close-modal')
        })
      } else {
        this.updateOrder()
      }
    },

    /** 
     * Title        : 배송상품 수정 실행
     * Status       : Actived
     * Description  : 조회된 배송상품의 정보를 수정한다
     *                Order number가 부여되지 않은 경우 Order number의 자동 채번또는 직접입력을 유도하며 
     *                Order number가 빈값은 불가능하다.
     */ 
    async updateOrder() {
      // Pass through
      let payload = {
        id                : this.parcelInfo.id,
        partnerId         : this.parcelInfo.partnerId,
        freightMethod     : this.parcelInfo.freightMethod,
        orderId           : this.parcelInfo.orderId,
        status            : this.parcelInfo.status,
        courierCode       : this.parcelInfo.courierCode,
        courierNum        : this.parcelInfo.courierNum,
        shipperName       : this.parcelInfo.shipperName,
        shipperMobile     : this.parcelInfo.shipperMobile?.replaceAll(/-/g, '') || null,
        shipperAddress    : this.parcelInfo.shipperAddress,
        receiverName      : this.parcelInfo.receiverName,
        receiverTel       : this.parcelInfo.receiverTel,
        receiverMobile    : this.parcelInfo.receiverMobile?.replaceAll(/-/g, '') || null,
        receiverAddress1  : this.parcelInfo.receiverAddress1,
        receiverAddress2  : this.parcelInfo.receiverAddress2,

        pcc               : this.parcelInfo.pcc,
        orderMemo         : this.parcelInfo.orderMemo,
        receiverType      : this.parcelInfo.receiverType,
        needClearance     : this.parcelInfo.needClearance,
        customsCategory   : this.parcelInfo.customsCategory,
        siteUrl           : this.parcelInfo.siteUrl,
        
        // 박스 크기
        width             : this.parcelInfo.width,
        length            : this.parcelInfo.length,
        height            : this.parcelInfo.height,
        weight            : this.parcelInfo.weight,
        volumeWeight      : this.parcelInfo.volumeWeight,
        boxQty            : this.parcelInfo.boxQty,

        ecommerceType     : this.parcelInfo.ecommerceType,
        overSeasSellerCode: this.parcelInfo.overSeasSellerCode,
        overSeasSellerName: this.parcelInfo.overSeasSellerName,
        shippingAgencyCode: this.parcelInfo.shippingAgencyCode,
        shippingAgencyName: this.parcelInfo.shippingAgencyName,
        sellerAgencyCode  : this.parcelInfo.sellerAgencyCode,
        sellerAgencyName  : this.parcelInfo.sellerAgencyName,

        // 요청사항
        customsRequest    : this.parcelInfo.customsRequest,
        deliveryRequest   : this.parcelInfo.deliveryRequest,

        updateHistoryFlag : this.parcelInfo.status !== this.originalStatus ? 'Y' : 'N',

        itemList          : this.itemList,
      };
  
      let config = {
        method  : "POST",
        url     : "/api/parcel/updateOrder",
        data    : payload,
        name    : "[ Axios / ParcelInfoForm / 상품정보수정 ]"
      }
  
      await this.$http(config)
        .then(() => {
          this.$emit('refresh-list')         
          this.$q.notify({ type: 'info', message: '상품정보가 수정되었습니다.' }) 
        })
        .catch((err) => {
          this.$log.debug(err)
          this.$q.notify({ type: 'negative', message: '수정에 실패하였습니다.' }) 
      })
    },


    

  
    /** 
     * Title        : 배송상품정보 모달 닫기
     * Status       : Actived
     * Description  : 현재모달을 닫는다
     */ 
    closeModalThis(){
      this.$emit('close-modal')
    },
  
    /** 
     * Title        : 고객변경
     * Status       : Suspended
     * Description  : 상품에 할당된 고객을 변경한다
     */ 
    changeCustomer(param){
      this.$log.debug("[ Parcel info form / 고객변경 ] = ", param)
  
      this.parcelInfo.customerName  = param.name
      this.parcelInfo.customerNum   = param.id
    },
  
    /** 
     * Title        : 등록고객 변경 모달 호출
     * Status       : Suspended
     * Description  : 고객변경모달을 호출한다
     */ 
    customerSearch(){
      this.customerModal = true
    },
  
    /** 
     * Title        : 고객조회 모달 닫기
     * Status       : Suspended
     * Description  : 고객조회 모달을 닫는다
     */
    closeModalSearch(){
      this.customerModal = false
    },

    /**
     * Title        : 볼륨중량 계산
     * Status       : Actived
     * Description  : 볼륨중량 계산
     * @param item 
     */
     calculateVolumeWeight(item){
      const { length, width, height } = item
      return Number(((length * width * height) / 6000).toFixed(3))
    },

    /**
     * Title        : 행 삭제
     * Status       : Actived
     * Description  : 행 삭제
     * @param row 
     */
    removeRow(row) {
      if (this.itemList.length > 1) {
        // Remove the row from the itemList
        const index = this.itemList.indexOf(row)
        if (index !== -1) {
          this.itemList.splice(index, 1)
        }
      } else {
        alert("최소 1개 이상의 상품이 필요합니다.")
      }
    },

    /** 
     * Title        : 부피무게 자동계산
     * Status       : Actived
     * Description  : width, height, length 수정 시 부피무게 자동계산
     */ 
    calculate (val) {
      // this.$log.debug("[ Parcel info form / Watch ] val = ", val)

      let data = Object.values(val)

      // this.$log.debug("[ Parcel info form / Watch ] data = ", data)

      // Dimension 을 곱한다.
      let result = data.reduce((acc, cur)=>{

        // this.$log.debug("[ Parcel info form / Watch ] acc = ", acc)
        // this.$log.debug("[ Parcel info form / Watch ] cur = ", cur)

        return acc * cur
      })
      
      // 항공과 해상의 비율
      const flight = 6000
      const ocean = 1000000
      
      this.$log.debug("flightWeight = " + (result/flight))
      this.$log.debug("oceanWeight = " + (result/ocean))

      let decimalLength = 100

      let calcualtion = function(result, ratio){
        return Math.round((result/ratio) * decimalLength) / decimalLength
      }

      // 항공/해상에 따라 부피무게가 계산이 된다
      if(this.parcelInfo.freightMethod == "1") {
        this.parcelInfo.volumeWeight = calcualtion(result, ocean)
      } else {
        this.parcelInfo.volumeWeight = calcualtion(result, flight)
      }
    },


    /**
     * Title        : 삭제
     * Status       : Actived
     * Description  : 현재 오더를 삭제한다.
     */
    async askDelete () {

      this.$q.dialog({
        title: '확인',
        message: '삭제하시겠습니까?<br />삭제 이후에는 관리자에게 문의하셔야 합니다.',
        cancel: true, // 취소 버튼 보임
        persistent: true, // ESC로 닫기 방지 (옵션)
        html: true
      }).onOk(() => {
        let payload = {
          id : [ this.parcelInfo.id ]
        }
        this.delete(payload, '1')
      });
    },


    /**
     * Title        : 삭제/복구 실행 메소드
     * Status       : Actived
     * Description  : 선택된 화물 삭제/복구 실행한다.
     */
    async delete (payload, type) {

      let newPayload = Object.assign( payload, { isDel : type }); 

      let config = {
        method  : "POST",
        url     : "/api/parcel/delete",
        data    : newPayload,
        name    : "[ Axios / ParcelList / 배송상품 삭제 실행 ]"
      }


      await this.$http(config)
        .then(() => {
          this.$log.debug("삭제했습니다.")

          this.$emit('reload-parcel-list')

          this.$q.notify({
            type: 'positive',
            message: this.isDel == '0' ? "삭제되었습니다" : "복구되었습니다",
            position: 'top',
            timeout: 3000,
          })
        })
        .catch((err) => {
          this.$log.debug(err)
          alert('불러오기 실패!')
      })
    },


    /**************************
     * PDF 관련기능
     **************************/
    async pdfModalControl(){
      const ok = await this.$refs.orderForm.validate()
      if (!ok) {
        this.$q.notify({ type: 'negative', message: '필수 항목을 확인해 주세요.' })
        return
      }
      if (!this.hasPrintableWeight(this.parcelInfo.weight)) {
        this.$q.notify({
          type: 'negative',
          message: '실제 무게가 입력되지 않아 운송장을 출력할 수 없습니다. 오더 상세에서 실제 무게를 입력한 뒤 다시 출력해 주세요.',
          position: 'top',
          timeout: 5000,
        })
        return
      }
      this.pdfModalShow = !this.pdfModalShow
    },

    /**
     * Title        : 페이지 초기화
     * Status       : Actived
     * Description  : 페이지 초기화
     */
    init() {
      // TODO: 배송상품 위치목록 - 사용하지 않는 것으로 보여 삭제한다.
      // this.$getParcelLocationList()
      // TODO: 픽업 목록 - 사용하지 않는 것으로 보여 삭제한다.
      // this.$getPickupLocationList()

      // 관리자 페이지 이므로 관리자가 아닌 경우 일반사이트로 이동을 시킨다.
      this.authLevel =  this.$getAuthLevel()

      // 택배사 목록
      this.$getCourierList()
      
      // 목적지(도시) 
      this.$getCodeList('CIT')
      // this.$getCityList()

      // 운송수단
      // this.$getCodeList('FREIGH')
      this.$getFreightList()
      
      // 오더상태 코드목록
      this.$getCodeList('ORDSTT')

      // 전자상거래유형 코드목록
      this.$getCodeList('BIZTYP')
    
      // Order 내 Item 상품 목록 조회
      this.getItemList()
      // 상태변경 이력조회
      this.getOrderHistory()
    },
  },

  mounted(){
    this.init()
  },
}

</script>


<template>

  <q-layout 
    container
    class="lg-width"
    view="lHh lpr lFf">

    <q-header 
      class="bg-primary"
      elevated>
      <q-toolbar>
        <q-toolbar-title>
          오더등록정보
        </q-toolbar-title>
        <q-btn flat v-close-popup round dense icon="close" @click="closeModalThis" />
      </q-toolbar>
    </q-header>

    <q-form ref="orderForm">
      <q-page-container>
        <q-page
          padding>

          <section class="order-summary" aria-labelledby="order-summary-title">
            <div class="order-summary__identity">
              <div id="order-summary-title" class="text-h6 text-weight-bold">오더 운영 요약</div>
              <div class="text-caption text-grey-7">
                Order No. {{ parcelInfo.orderId || '-' }} · H.B/L {{ parcelInfo.hblNo || '-' }}
              </div>
            </div>
            <div class="order-summary__statuses">
              <div class="summary-status">
                <span>진행</span>
                <q-badge :color="orderStatusColor" :label="orderStatusLabel" />
              </div>
              <div class="summary-status">
                <span>포장</span>
                <q-badge :color="packingStatusColor" :label="packingStatusLabel" />
              </div>
              <div class="summary-status">
                <span>콘솔</span>
                <q-badge :color="consoleStatusColor" :label="consoleStatusLabel" />
              </div>
            </div>
            <div class="order-summary__meta text-caption">
              <span><q-icon name="person" /> {{ parcelInfo.customerName || '-' }}</span>
              <span><q-icon name="business" /> {{ parcelInfo.partnerName || '-' }}</span>
              <span><q-icon name="event" /> {{ parcelInfo.modiDate || parcelInfo.regDate || '-' }}</span>
              <span><q-icon name="tag" /> ID {{ parcelInfo.id }}</span>
            </div>
          </section>

          <nav class="section-nav" aria-label="오더등록정보 섹션 이동">
            <q-btn flat dense no-caps label="주문·작업" @click="scrollToSection('order-operation')" />
            <q-btn flat dense no-caps label="화물·박스" @click="scrollToSection('cargo-info')" />
            <q-btn flat dense no-caps label="보내는 사람" @click="scrollToSection('shipper-info')" />
            <q-btn flat dense no-caps label="받는 사람" @click="scrollToSection('receiver-info')" />
            <q-btn flat dense no-caps label="상품" @click="scrollToSection('item-info')" />
            <q-btn flat dense no-caps label="배송·요청" @click="scrollToSection('delivery-info')" />
            <q-btn flat dense no-caps label="변경 이력" @click="scrollToSection('history-info')" />
          </nav>

          <section id="order-operation" class="order-section">
            <div class="section-heading">
              <div>
                <div class="text-subtitle1 text-weight-bold">주문 및 작업 상태</div>
                <div class="text-caption text-grey-7">주문 진행 상태와 주요 식별 정보를 확인합니다.</div>
              </div>
            </div>

          <div class="q-pa-md">
            <div class="form-column-wrap">
              <template v-if="authLevel === '80'">
                <q-select 
                  v-model="parcelInfo.status"
                  :options="orderStatusCodeListForPartner"
                  behavior="menu"
                  label="진행상태"
                  emit-value
                  map-options
                  :disable="Number(originalStatus) >= 2" />
              </template>

              <template v-if="Number(authLevel) > 80">
                <q-select 
                  v-model="parcelInfo.status"
                  :options="this.$store.getters.getOrderStatusCodeList"
                  behavior="menu"
                  label="진행상태"
                  emit-value
                  map-options />
              </template>

              <q-input 
                v-model="parcelInfo.mblNo"
                label="M.B/L번호" 
                disable />

              <q-input
                :model-value="packingOwnerLabel"
                label="포장주체"
                disable />

              <q-select 
                v-model="parcelInfo.freightMethod"
                :options="this.$store.getters.getFreightMethodList"
                behavior="menu"
                label="운송수단"
                emit-value
                map-options
                disable />
            </div>

            <div class="form-column-wrap">

              <q-input 
                v-model="parcelInfo.orderId"
                label="Order No."
                :disable="!editable" />

              <q-input 
                v-model="parcelInfo.round"
                label="회차"
                disable />

              <q-input 
                v-model="parcelInfo.hblNo"
                label="H.B/L번호"
                disable />

              </div>
            </div>

          <div class="q-pa-md">
            <div class="form-column-wrap">
              <q-input 
                v-model="parcelInfo.regName"
                label="등록자명"
                disable />
    
              <q-input 
                v-model="parcelInfo.partnerName"
                label="파트너명"
                disable />

              <q-input 
                v-model="parcelInfo.customerName"
                label="고객명"
                disable />

              <q-btn 
                v-show="!editable" 
                label="고객조회" 
                @click="customerSearch" 
                color="primary" />

            </div>
          </div>

          <div class="packing-action-card">
            <div class="packing-action-card__summary">
              <div class="text-caption text-grey-7">현재 포장상태</div>
              <div class="row items-center q-gutter-sm">
                <q-badge :color="packingStatusColor" text-color="white" class="status-badge">
                  {{ packingStatusLabel }}
                </q-badge>
                <span class="text-caption text-grey-7">담당: {{ packingOwnerLabel }}</span>
              </div>
            </div>

            <div v-if="isAdmin" class="packing-action-card__controls">
              <q-btn
                :label="pendingPackingCancellation ? '같은 요청 결과 확인' : '포장완료 취소'"
                color="primary"
                unelevated
                :loading="packingStatusUpdating"
                :disable="(!canCancelPacking && !pendingPackingCancellation) || packingStatusUpdating"
                @click="confirmPackingStatusChange" />
              <span class="text-caption text-grey-7">AONE 포장완료·콘솔 미연결 주문을 포장대기로 되돌립니다.</span>
            </div>
            <div v-else class="text-caption text-grey-7">
              포장완료 취소는 관리자만 처리할 수 있습니다.
            </div>
          </div>
          </section>

          <section id="cargo-info" class="order-section">
            <div class="section-heading">
              <div>
                <div class="text-subtitle1 text-weight-bold">화물 및 박스 정보</div>
                <div class="text-caption text-grey-7">크기, 중량과 전자상거래 부가정보를 관리합니다.</div>
              </div>
            </div>

          <!-- 박스 크기  -->
          <div class="q-pa-md">
            <h6 class="q-mb-none q-mt-sm">박스 크기</h6>
          </div>
          <div class="q-pa-md">
            <div class="form-column-wrap">
              <q-input 
                bottom-slots 
                v-model="parcelInfo.width" 
                label="가로 : "
                :disable="!editable">
                <template v-slot:append>
                  cm
                </template>
              </q-input>

              <q-input 
                bottom-slots 
                v-model="parcelInfo.length" 
                label="세로 : "
                :disable="!editable">
                <template v-slot:append>
                  cm
                </template>
              </q-input>

              <q-input 
                bottom-slots 
                v-model="parcelInfo.height" 
                label="높이 : "
                :disable="!editable">
                <template v-slot:append>
                  cm
                </template>
              </q-input>
            </div>
          
            <div class="form-column-wrap">
                
              <q-input 
                v-model="parcelInfo.volumeWeight" 
                bottom-slots 
                label="부피 무게 : "
                readonly
                :disable="!editable">
                <template v-slot:append>
                  {{ this.orderVolumeWeight }}
                  {{ parcelInfo.freightMethod == '1' ? 'CBM' : 'kg' }}
                </template>
              </q-input>
              
              <q-input 
                v-model="parcelInfo.weight" 
                type="number"
                label-slot
                lazy-rules
                :rules="[val => val === null || val === undefined || val === '' || Number(val) >= 0 || '실제 무게는 0 이상이어야 합니다.']"
                :disable="!editable">
                <template #label>
                  <span 
                    class="custom-label">
                    실제 무게
                  </span>
                </template>
                <template v-slot:append>
                  kg
                </template>
              </q-input>
              <q-input 
                v-model="parcelInfo.boxQty" 
                label="박스수량 : "
                type="number"
                min="1"
                :disable="!editable">
              </q-input>
            </div>
            <p>부피무게는 가로x세로x높이x(항공 또는 해상)의 값에 따라 자동으로 계산됩니다.</p>
          </div>

          <!-- // 유형별 추가정보 -->
          <div class="q-pa-md">
            <h6 class="q-mb-none q-mt-sm">유형별 추가정보</h6>
          </div>
          <div class="q-pa-md">
            <div class="form-column-wrap">
              <q-select 
                v-model="parcelInfo.ecommerceType"
                :options="this.$store.getters.getBizTypeCodeList"
                behavior="menu"
                emit-value
                map-options
                label="전자상거래 유형 코드"
                type="text" 
                :disable="!editable"/>

              <q-input 
                v-model="parcelInfo.overSeasSellerCode"
                label="해외판매자 부호"
                type="text" 
                :disable="!editable"/>

              <q-input 
                v-model="parcelInfo.overSeasSellerName"
                label="해외판매자 상호"
                type="text"
                :disable="!editable" />

              <q-input 
                v-model="parcelInfo.shippingAgencyCode"
                label="구매/배송대행업체 부호"
                type="text"
                :disable="!editable" />

              <q-input 
                v-model="parcelInfo.shippingAgencyName"
                label="구매/배송대행업체 상호"
                type="text"
                :disable="!editable" />

              <q-input 
                v-model="parcelInfo.sellerAgencyCode"
                label="판매대행업체 부호"
                type="text"
                :disable="!editable" />
              
              <q-input 
                v-model="parcelInfo.sellerAgencyName"
                label="판매대행업체 상호"
                type="text"
                :disable="!editable" />
            </div>
          </div>
          <div class="q-pa-md">
            <div class="form-column-wrap">
              <q-input 
                v-model="parcelInfo.siteUrl"
                label="홈페이지"
                type="text"
                :disable="!editable" />
            </div>
          </div>
          </section>

          <section id="shipper-info" class="order-section">
            <div class="section-heading">
              <div>
                <div class="text-subtitle1 text-weight-bold">보내는 사람 정보</div>
                <div class="text-caption text-grey-7">발송자 연락처와 출발지 주소를 확인합니다.</div>
              </div>
            </div>

          <div class="q-pa-md">
            <div class="form-column-wrap">
              <q-input 
                v-model="parcelInfo.shipperName"
                label="보내는 사람명"
                :disable="!editable" />

              <q-input 
                v-model="parcelInfo.shipperMobile"
                label="보내는 사람 휴대폰번호"
                mask="###-####-####"
                :disable="!editable" />
              
            </div>

            <div class="form-column-wrap">

              <q-input
                v-model="parcelInfo.shipperAddress"
                label="보내는 사람주소"
                filled
                type="textarea"
                rows="2"
                :disable="!editable" />

            </div>

            <div 
              v-if="parcelInfo.pickUpLocation === 'AONEPI03'" 
              class="form-column-wrap">
              
              <q-input
                v-model="parcelInfo.deliveryAddress"
                label="배송요청주소"
                filled
                type="textarea"
                rows="2"
                :disable="!editable" />

            </div>
          </div>
          </section>

          <section id="receiver-info" class="order-section">
            <div class="section-heading">
              <div>
                <div class="text-subtitle1 text-weight-bold">받는 사람 정보</div>
                <div class="text-caption text-grey-7">통관번호, 수취인 연락처와 배송지 정보를 관리합니다.</div>
              </div>
            </div>
          <div class="q-pa-md">
            <div class="form-column-wrap q-mb-md">
              <q-input 
                v-model="parcelInfo.pcc"
                label-slot
                lazy-rules
                :rules="[val => !!val || '* 표시 항목은 필수 입력입니다.']"
                :disable="!editable" >
                <template #label>
                  <span :class="['custom-label', { required: !parcelInfo.pcc }]">
                    개인고유통관번호 *
                  </span>
                </template>
              </q-input>

              <q-input 
                v-model="parcelInfo.receiverName"
                label-slot
                lazy-rules
                :rules="[val => !!val || '* 표시 항목은 필수 입력입니다.']"
                :disable="!editable" >
                <template #label>
                  <span :class="['custom-label', { required: !parcelInfo.receiverName }]">
                    받는 사람명 *
                  </span>
                </template>
              </q-input>

              <q-input 
                v-model="parcelInfo.receiverMobile"
                label-slot
                mask="###-####-####"
                lazy-rules
                :rules="[val => !!val || '* 표시 항목은 필수 입력입니다.']"
                :disable="!editable" >
                <template #label>
                  <span :class="['custom-label', { required: !parcelInfo.receiverMobile }]">
                    받는 사람 휴대폰번호 *
                  </span>
                </template>
              </q-input>

              <q-input 
                v-model="parcelInfo.receiverZipcode"
                label-slot
                lazy-rules
                :rules="[val => !!val || '* 표시 항목은 필수 입력입니다.']"
                :disable="!editable" >
                <template #label>
                  <span :class="['custom-label', { required: !parcelInfo.receiverZipcode }]">
                    받는 사람 우편번호 *
                  </span>
                </template>
              </q-input>
              
            </div>

            <div class="form-column-wrap q-mb-md">

              <q-input
                v-model="parcelInfo.receiverAddress1"
                label-slot
                filled
                type="textarea"
                rows="2"
                :disable="!editable">
                <template #label>
                  <span :class="['custom-label', { required: !parcelInfo.receiverAddress1 }]">
                    받는 사람주소 *
                  </span>
                </template>
              </q-input>

            </div>

            <div class="form-column-wrap">

              <q-input
                v-model="parcelInfo.receiverAddress2"
                label-slot
                filled
                :disable="!editable">
                <template #label>
                  <span :class="['custom-label', { required: !parcelInfo.receiverAddress2 }]">
                    받는 사람 상세주소 *
                  </span>
                </template>
              </q-input>

            </div>

          </div>
          </section>

          <section id="item-info" class="order-section">
            <div class="section-heading">
              <div>
                <div :class="['text-subtitle1 text-weight-bold', { 'text-negative': itemList.length === 0 }]">
                  상품 목록
                </div>
                <div class="text-caption text-grey-7">
                  {{ itemList.length }}개 품목 · 총 수량 {{ itemTotalQty }}개 · 총 금액 {{ itemAmountSummary }}
                </div>
              </div>
              <q-btn
                outline
                color="primary"
                icon="add"
                label="상품 추가"
                @click="() => {itemList.push({ ...defaultItemSet})}"
                :disable="!editable" />
            </div>

          <div class="q-pa-md">
            <q-table
              flat
              bordered
              :separator="separator"
              :rows-per-page-options="[0]"
              :rows="itemList"
              :columns="itemColumns"
              row-key="id">
    
              <!-- 모든 입력 셀을 커스터마이징 -->
              <template v-slot:body-cell-barcode="props">
                <q-td style="min-width: 180px;">
                  <q-input
                    v-model="props.row.barcode"
                    dense
                    borderless
                    hide-bottom-space
                    :disable="!editable"
                  />
                </q-td>
              </template>

              <template v-slot:body-cell-itemName="props">
                <q-td style="min-width: 300px;">
                  <q-input 
                    v-model="props.row.itemName"
                    dense 
                    borderless 
                    lazy-rules
                    :rules="[
                      v => !!v || '상품명은 필수입니다.',
                      this.$validationSet.itemName
                    ]"
                    hide-bottom-space
                    :disable="!editable"/>
                </q-td>
              </template>

              <template v-slot:body-cell-brand="props">
                <q-td style="min-width: 200px;">
                  <q-input 
                    v-model="props.row.brand" 
                    dense 
                    borderless 
                    lazy-rules
                    hide-bottom-space
                    :disable="!editable"
                    :rules="[
                      v => !!v || '브랜드는 필수입니다.',
                      this.$validationSet.alphaNum
                    ]"/>
                </q-td>
              </template>

              <template v-slot:body-cell-itemQty="props">
                <q-td style="text-align: right; min-width: 80px;">
                  <q-input 
                    v-model.number="props.row.itemQty" 
                    type="number" 
                    dense 
                    borderless
                    maxlength="5" 
                    min="1"
                    lazy-rules
                    :rules="[
                      v => v !== null && v !== undefined || '수량은 필수입니다.',
                      v => Number(v) > 0 || '수량은 1 이상이어야 합니다.'
                    ]"
                    hide-bottom-space
                    :disable="!editable" />
                </q-td>
              </template>

              <template v-slot:body-cell-unit="props">
                <q-td style="text-align: right; min-width: 80px;">
                  <q-input 
                    v-model="props.row.unit" 
                    dense 
                    borderless 
                    maxlength="5"
                    :disable="!editable" />
                </q-td>
              </template>

              <template v-slot:body-cell-currency="props">
                <q-td style="min-width: 100px;">
                  <q-select
                    v-model="props.row.currency"
                    :options="['USD', 'KRW', 'JPY']"
                    dense
                    borderless
                    emit-value
                    map-options
                    :disable="!editable"
                  />
                </q-td>
              </template>

              <template v-slot:body-cell-unitPrice="props">
                <q-td style="min-width: 100px;">
                  <q-input 
                    v-model.number="props.row.unitPrice" 
                    type="number" 
                    dense 
                    borderless 
                    min="0"
                    step="any"
                    input-class="text-right"
                    :disable="!editable" />
                </q-td>
              </template>

              <template v-slot:body-cell-weight="props">
                <q-td style="min-width: 100px;">
                  <q-input 
                    v-model.number="props.row.weight" 
                    type="number" 
                    dense 
                    borderless 
                    lazy-rules
                    input-class="text-right"
                    :rules="[
                      v => v === null || v === undefined || v === '' || Number(v) >= 0 || '무게는 0 이상이어야 합니다.'
                    ]"
                    :disable="!editable" />
                </q-td>
              </template>

              <template v-slot:body-cell-height="props">
                <q-td style="min-width: 100px;">
                  <q-input 
                    v-model.number="props.row.height" 
                    type="number" 
                    dense 
                    borderless 
                    input-class="text-right"
                    :disable="!editable" />
                </q-td>
              </template>

              <template v-slot:body-cell-length="props">
                <q-td style="min-width: 100px;">
                  <q-input 
                    v-model.number="props.row.length" 
                    type="number" 
                    dense 
                    borderless 
                    input-class="text-right"
                    :disable="!editable" />
                </q-td>
              </template>

              <template v-slot:body-cell-width="props">
                <q-td style="min-width: 100px;">
                  <q-input 
                    v-model.number="props.row.width" 
                    type="number" 
                    dense 
                    borderless
                    input-class="text-right"
                    :disable="!editable" />
                </q-td>
              </template>

              <template v-slot:body-cell-volumeWeight="props">
                <q-td class="text-right">
                  {{ calculateVolumeWeight(props.row) }}
                </q-td>
              </template>

              <template v-slot:body-cell-url="props">
                <q-td style="min-width: 100px;">
                  <q-input 
                    v-model="props.row.url" 
                    dense 
                    borderless
                    :disable="!editable" />
                </q-td>
              </template>

              <template v-slot:body-cell-hsCode="props">
                <q-td style="min-width: 150px;">
                  <q-input 
                    v-model="props.row.hsCode" 
                    dense 
                    type="text"
                    borderless
                    maxlength="6"
                    hide-bottom-space
                    :disable="!editable"
                    :rules="[
                      v => !v || v.length === 6 || 'HS CODE는 6자리입니다.',
                      this.$validationSet.num,
                    ]" />
                </q-td>
              </template>


              <template v-slot:body-cell-action="props">
                <q-td class="text-center">
                  <q-btn
                    dense
                    flat
                    color="negative"
                    icon="delete"
                    aria-label="상품 삭제"
                    @click="removeRow(props.row)"
                    :disable="!editable"
                  />
                </q-td>
              </template>

              <template 
                v-slot:bottom>
                <div class="col col-12 text-right text-h6">
                  총 금액 : <span :class="itemAmountWarning ? 'text-orange': ''">{{ itemAmountSummary }}</span>
                </div>
              </template>

            </q-table>
          </div>
          </section>

          <section id="delivery-info" class="order-section">
            <div class="section-heading">
              <div>
                <div class="text-subtitle1 text-weight-bold">배송 및 요청사항</div>
                <div class="text-caption text-grey-7">통관 유형, 배송업체와 작업 요청을 관리합니다.</div>
              </div>
            </div>

          <div class="q-pa-md">
            <div class="form-column-wrap">
              <q-select 
                v-model="parcelInfo.needClearance" 
                :options="[
                  { label: '목록', value: 'Y' },
                  { label: '일반', value: 'N' },
                ]"
                emit-value
                map-options
                label-slot
                lazy-rules
                :rules="[val => !!val || '* 표시 항목은 필수 입력입니다.']"
                behavior="menu"
                :disable="!editable" >
                <template #label>
                  <span :class="['custom-label', { required: !parcelInfo.needClearance }]">
                    일반신청 *
                  </span>
                </template>
              </q-select>
            </div>
          </div>

          <div class="q-pa-md">
            <div class="form-column-wrap">

              <q-select
                v-model="parcelInfo.courierCode"
                label="배송업체"
                :options="this.$store.getters.getCourierList"
                behavior="menu"
                emit-value
                map-options
                :disable="!editable" />

              <q-input 
                v-model="parcelInfo.courierNum"
                label="운송장번호"
                :disable="!editable" />

              <!--
              <q-select 
                v-model="parcelInfo.parcelLocation"
                :options="this.$store.getters.getParcelLocationListNew"
                behavior="menu"
                label="배송상품 위치"
                emit-value
                map-options
                :disable="user === 'cus'" />
              -->

            </div>
          </div>

          <div class="q-pa-md">
            <div class="form-column-wrap">
              <q-input 
                v-model="parcelInfo.orderMemo"
                bottom-slots
                ref="note"
                label="고객메모"
                type="textarea"
                placeholder="배송물품 정보를 적어주세요. (의류/전자기기/가전)"
                :rows="2"
                :disable="!editable" />


              <q-input 
                v-model="parcelInfo.customsRequest"
                bottom-slots
                ref="note"
                label="통관시 요청사항"
                type="textarea"
                placeholder="배송물품 정보를 적어주세요. (의류/전자기기/가전)"
                :rows="2"
                :disable="!editable" />

                <q-input 
                v-model="parcelInfo.deliveryRequest"
                bottom-slots
                ref="note"
                label="배송 요청사항"
                type="textarea"
                placeholder="배송물품 정보를 적어주세요. (의류/전자기기/가전)"
                :rows="2"
                :disable="!editable" />

            </div>
          </div>
          </section>

          <section id="history-info" class="order-section">
            <div class="section-heading">
              <div>
                <div class="text-subtitle1 text-weight-bold">상태변경 이력</div>
                <div class="text-caption text-grey-7">일반 정보변경은 이력에 저장되지 않습니다.</div>
              </div>
            </div>

          <div class="row q-pa-md">
            <div class="form-column-wrap col-md-6 col-xs-12">
              <q-table
                class="my-sticky-virtscroll-table"
                style="min-width: 250px; width: 100%; max-width: 100%;"
                virtual-scroll
                :rows-per-page-options="[0]"
                :virtual-scroll-sticky-size-start="48"
                :rows="historyList"
                :columns="historyColumns"
                row-key="id">

              </q-table>
            </div>
          </div>
          </section>

        </q-page>
      </q-page-container>

      <q-footer class="bg-white text-black footer-actions">
        <div class="footer-actions__inner">
          <q-btn
            v-show="editable"
            icon="delete"
            label="주문 삭제"
            outline
            color="negative"
            @click="askDelete" />

          <q-space />

          <q-btn
            v-if="user === 'admin'"
            icon="print"
            label="운송장 출력"
            flat
            color="info"
            @click="pdfModalControl" />

          <q-btn
            label="닫기"
            flat
            color="grey-8"
            @click="closeModalThis" />

          <q-btn
            v-show="editable"
            icon="save"
            label="변경사항 저장"
            unelevated
            color="secondary"
            type="button"
            @click="onSubmit" />
        </div>
      </q-footer>
    </q-form>
  </q-layout>


  <!-------------------- 
    MODALS 
  --------------------->
  <teleport to="body">
		<q-dialog
			v-model="customerModal">
      <ModalCustomerSearch
        @change-customer="changeCustomer"
        @close-modal-search="closeModalSearch" />
    </q-dialog>
	</teleport>

  <teleport to="body">
		<q-dialog 
      v-model="pdfModalShow"
      persistent>

      <PdfPreviewer
        style="width: 100%;"
        :parcelList="selectedParcel"
        @close-modal="pdfModalControl"/>

    </q-dialog>
	</teleport>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
h5 {
  margin-block-end: 0.5rem;
}

.btn {
  border-radius: 0px;
}

.delete {
  flex: auto;
}

.no-gutter {
  padding : 0px;
}

.lg-width {
  width : 70vw;
  max-width: 1024px;
}

.custom-label.required {
  color: #e53935; /* 붉은색 */
}
.custom-label {
  // color: #555; /* 기본 라벨 색상 */
  color: #e53935; /* 붉은색 */
  font-weight:700;
}

@media (min-width: 320px){
  .lg-width {
      width : 90vw;
      max-width: inherit;
  }
}

@media (min-width: 600px){
  .lg-width {
      width : 90vw;
      max-width: inherit;
  }
}

@media (min-width: 1024px){
  .lg-width {
      width : 70vw;
      max-width: 1024px;
  }
}

.form-column-wrap {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  gap: 30px;

  .q-field {
    flex: 1;
    flex-basis: 150px;
    // min-width: 150px;
  }

  .mobile-country {
    flex-basis: 120px;
    min-width: 110px;
    max-width: 120px;
  }
}

.order-summary {
  display: grid;
  grid-template-columns: minmax(220px, 1.4fr) minmax(280px, 1fr);
  gap: 18px 32px;
  padding: 22px 24px;
  margin-bottom: 12px;
  border: 1px solid #dfe5ec;
  border-radius: 14px;
  background: linear-gradient(135deg, #f7faff 0%, #ffffff 70%);
}

.order-summary__statuses,
.order-summary__meta,
.packing-action-card,
.packing-action-card__controls,
.footer-actions__inner {
  display: flex;
  align-items: center;
}

.order-summary__statuses {
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;
}

.summary-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #5f6b7a;
}

.order-summary__meta {
  grid-column: 1 / -1;
  flex-wrap: wrap;
  gap: 10px 24px;
  padding-top: 14px;
  border-top: 1px solid #e9edf2;
}

.section-nav {
  position: sticky;
  top: 58px;
  z-index: 5;
  display: flex;
  gap: 4px;
  padding: 8px;
  margin-bottom: 16px;
  overflow-x: auto;
  border: 1px solid #e1e6ed;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 4px 14px rgba(30, 50, 80, 0.06);
}

.section-nav .q-btn {
  white-space: nowrap;
}

.order-section {
  scroll-margin-top: 112px;
  margin-bottom: 18px;
  overflow: hidden;
  border: 1px solid #dfe5ec;
  border-radius: 12px;
  background: #fff;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #e8edf3;
  background: #f8fafc;
}

.packing-action-card {
  justify-content: space-between;
  gap: 24px;
  padding: 18px 20px;
  margin: 0 16px 16px;
  border: 1px solid #cfdcf0;
  border-radius: 10px;
  background: #f5f9ff;
}

.packing-action-card__summary {
  min-width: 190px;
}

.packing-action-card__controls {
  justify-content: flex-end;
  gap: 10px;
  width: min(100%, 470px);
}

.packing-action-card__controls .q-field {
  flex: 1;
}

.status-badge {
  padding: 6px 10px;
  font-size: 13px;
}

.footer-actions {
  border-top: 1px solid #dfe5ec;
  box-shadow: 0 -4px 14px rgba(30, 50, 80, 0.08);
}

.footer-actions__inner {
  gap: 8px;
  padding: 12px 18px;
}

@media (max-width: 700px) {
  .order-summary {
    grid-template-columns: 1fr;
    padding: 18px;
  }

  .order-summary__statuses {
    justify-content: flex-start;
  }

  .order-summary__meta {
    grid-column: auto;
  }

  .packing-action-card,
  .packing-action-card__controls {
    align-items: stretch;
    flex-direction: column;
  }

  .packing-action-card__controls {
    width: 100%;
  }

  .section-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .footer-actions__inner {
    flex-wrap: wrap;
  }

  .footer-actions__inner .q-space {
    display: none;
  }
}
</style>
